import type { LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { type, button, surface } from '../theme.js';
import { cn } from '../../lib/cn.js';

/**
 * 空态。**七个页面的空态都必须走这个组件。**
 *
 * 两个理由:
 *
 * 1. 空态是这类产品第一印象的大半 —— 工厂刚开通的时候**每一页都是空的**,
 *    他看到的第一屏就是七块空白。一句「还没有询盘」等于告诉他「买了个空盒子」;
 *    一句「客户来消息会出现在这里,先去把 WhatsApp 连上」是在教他下一步做什么。
 *    所以 `hint` 和 `action` 不是可选的装饰,是这一屏的正文。
 *
 * 2. `data-testid="empty-state"` 刻在组件里,守卫用例按它数(七页各渲染一次空数据,
 *    每页至少一个)。靠人记得在每个空白 div 上加一个 testid = 迟早漏一页,
 *    而漏掉的那页恰好就是没人想过空的时候长什么样的那页。
 */
export function EmptyState({
  icon: Icon,
  title,
  hint,
  action,
  testId,
  className,
}: {
  icon?: LucideIcon;
  /** 一句话说明现在是什么状态。**不要写「暂无数据」** */
  title: string;
  /** 一句话说明接下来该干什么。没有下一步动作的时候可以省 */
  hint?: string;
  /**
   * 有下一步就给个按钮。`to` 走站内路由,`onClick` 走当页动作。
   * `primary` 只给「这一屏就是要他点这一下」的场景(比如没连过 WhatsApp)——
   * 大部分空态的动作是「去别处看看」,那种用次级按钮就够。
   */
  action?: { label: string; to?: string; onClick?: () => void; primary?: boolean };
  /** 页面自己再加一个更具体的 testid(比如区分「全部为空」和「筛选后为空」) */
  testId?: string;
  className?: string;
}): JSX.Element {
  return (
    <div
      className={cn(surface.card, 'px-6 py-12 text-center', className)}
      data-testid="empty-state"
    >
      {Icon ? <Icon className="mx-auto mb-3 h-8 w-8 text-portal-mute" aria-hidden /> : null}
      <p className={cn(type.lead, 'font-medium')} data-testid={testId}>
        {title}
      </p>
      {hint ? <p className={cn(type.caption, 'mx-auto mt-1.5 max-w-sm')}>{hint}</p> : null}
      {action ? (
        <div className="mt-4">
          {action.to ? (
            <Link to={action.to} className={action.primary ? button.primary : button.secondary}>
              {action.label}
            </Link>
          ) : (
            <button
              type="button"
              onClick={action.onClick}
              className={action.primary ? button.primary : button.secondary}
              data-testid="empty-state-action"
            >
              {action.label}
            </button>
          )}
        </div>
      ) : null}
    </div>
  );
}
